import { reject } from 'ramda';
import { Mutation, Action, Getter } from 'vuex';
import { paramCase } from 'change-case';

import * as types from '../mutation-types';
import { RootState, ModalState, ModalData } from '../types';
import { modalZIndex } from '@sass/globals.module.scss';

const getTestId = (type: string) => `modal-${paramCase(type)}`;

// The base zIndex is the default value + 1 so that modals handled by the store have
// have preference over local modals
const baseZIndex = parseInt(modalZIndex, 10) + 1;

// initial state
const initialState = {
  modals: [],
};

const getters: Record<string, Getter<ModalState, RootState>> = {
  topModal: (state) => (state.modals.length ? state.modals[state.modals.length - 1] : null),
};

// actions
const actions: { [key: string]: Action<ModalState, RootState> } = {
  show({ commit, state }, data: ModalData) {
    if (state.modals.find((activeModal) => activeModal.type === data.type)) {
      // modals are unique by type
      return false;
    }

    commit(types.MODAL_SHOW, {
      type: data.type,
      payload: data.payload,
      stackable: data.stackable || false,
    });

    return true;
  },

  /**
   * Refresh an open modal's payload. `show` is a no-op while a modal of the same type is open, so a
   * caller that reopens an already-visible modal with fresh data uses this to hand it over.
   */
  updatePayload({ commit, state }, data: ModalData) {
    if (!state.modals.find((activeModal) => activeModal.type === data.type)) {
      return false;
    }

    commit(types.MODAL_UPDATE_PAYLOAD, { type: data.type, payload: data.payload });

    return true;
  },

  hide({ commit, state }, data) {
    if (state.modals.find((activeModal) => activeModal.type === data.type)) {
      commit(types.MODAL_HIDE, {
        type: data.type,
        reason: data.reason,
        payload: data.payload,
      });
    }
  },
};

// mutations
const mutations: { [key: string]: Mutation<ModalState> } = {
  [types.MODAL_SHOW](currentState, data) {
    const modal = {
      type: data.type,
      payload: data.payload,
    };

    if (data.stackable && currentState.modals.length) {
      currentState.modals = currentState.modals.flatMap((existingModal, index) => {
        const updatedModal = { ...existingModal, zIndex: baseZIndex + index };

        if (index === currentState.modals.length - 1) {
          // Add the new modal
          return [
            updatedModal,
            {
              ...modal,
              zIndex: baseZIndex + index + 1,
              tId: getTestId(modal.type),
            },
          ];
        }

        return updatedModal;
      });
    } else {
      currentState.modals = [{ ...modal, zIndex: baseZIndex, tId: getTestId(modal.type) }];
    }
  },

  [types.MODAL_UPDATE_PAYLOAD](currentState, data) {
    currentState.modals = currentState.modals.map((modal) =>
      modal.type === data.type ? { ...modal, payload: data.payload } : modal
    );
  },

  [types.MODAL_HIDE](currentState, data) {
    currentState.modals = reject((modal) => modal.type === data.type, currentState.modals);
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
  getters,
};
